/** GxTopBar (screens §2): wordmark, the bank and team, the round clock, the viewer's role and initials. */
import type { ReactNode } from "react";
import { GxAvatar } from "../theme/Avatar";
import { GxMenu, type MenuItem } from "../theme/Menu";

const ROLE_LABELS: Readonly<Record<string, string>> = { ceo: "CEO", cro: "Risk officer", cfo: "CFO", cco: "CCO", coo: "COO", treasurer: "Treasurer", commercial: "Commercial", retail: "Retail", member: "Team" };

export const roleLabel = (role: string): string => ROLE_LABELS[role] ?? role;


export function GxTopBar({ bankName, subline, role, name, clock, onSignOut, accountItems = [], acting = null, email = null, title, onMenu, menuOpen = false }: {
  /** The page's name for the phone bar (chrome only: the page's own heading is the document heading, so it is hidden from a screen reader here). */ readonly title?: string;
  /** Opens the menu sheet. With it, at 768 px and below the bar is the phone bar (menu button, title over bank and team, the clock); without it the bar keeps its desktop parts at any width. */ readonly onMenu?: () => void; readonly menuOpen?: boolean;
  /** The signed-in address, shown under the name in the account menu. */ readonly email?: string | null;
  /** The acting-as strip while a hand-over is live: it sits full width under the bar, not squeezed into it. */ readonly acting?: ReactNode;
  readonly bankName: string; readonly subline: string; readonly role: string | null; readonly name: string; readonly clock: ReactNode; readonly onSignOut?: () => void; readonly accountItems?: readonly MenuItem[];
}) {
  return (
    <>
    <header className="gx-topbar" {...(onMenu === undefined ? {} : { "data-menu": "true" })}>
      {onMenu === undefined ? null : (
        <button type="button" className="gx-tb-menu" aria-label="Menu" aria-controls="gx-navsheet" aria-expanded={menuOpen} aria-haspopup="dialog" data-testid="shell.menu" onClick={onMenu}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        </button>
      )}
      <div className="gx-brand">
        <div className="gx-mark" aria-hidden="true">G</div>
        <div><div className="gx-wordmark">Governexus</div><div className="gx-product">Bank Simulator</div></div>
      </div>
      <div className="gx-tb-sep" />
      <div className="gx-bankid"><b>{bankName}</b><span>{subline}</span></div>
      {onMenu === undefined ? null : (
        <div className="gx-tb-title" aria-hidden="true" data-testid="shell.title"><b>{title ?? bankName}</b><span className="gx-tb-sub"><span>{bankName}</span>{subline === "" || subline === title ? null : <span>· {subline}</span>}</span></div>
      )}
      <div className="gx-tb-spacer" />
      {clock}
      {role === null ? null : <span className="gx-role" data-testid="shell.role">{roleLabel(role)}</span>}
      {name === "" ? null : onSignOut === undefined ? <GxAvatar name={name} /> : (
        <GxMenu label="Account menu" testId="shell.account" align="right" className="gx-tb-account" heading={<><b>{name}</b>{email === null ? null : <span>{email}</span>}</>} trigger={<GxAvatar name={name} />} items={[...accountItems, { key: "signout", label: "Sign out", onSelect: onSignOut }]} />
      )}
    </header>
    {acting}
    </>
  );
}
